import {
  Applicationv2Tenancy,
  Contactcontactv2Company,
  Contactcontactv2Type,
  V2PhoneNumber,
  IndustryIndustry,
  SearchContactHit,
  SearchListContactsRequest,
  SearchListContactsResponse,
  DeviceLockingKey,
  ObjectdeviceDevice,
  ObjectroomRoom,
  Requestapplicationv2Company,
  Requestapplicationv2Employment,
  Requestapplicationv2Individual,
  SortSortOrder,
  StoreMedia,
  Templatev2Template,
  TicketChecklistItem,
  TicketChecklistTemplate,
  TicketCorrespondence,
  TicketTicket,
  TicketTicketLink,
  TicketticketCosts,
  TicketTicketReference,
  TypeDate,
  V2Account,
  V2User,
  V2CompanyContactPerson,
  V2PreferredCommunicationChannel,
  V2Person,
  V2ReferenceConfirmation,
  V2ReferenceFormAdministration,
  V2GetReferenceFormResponse,
  V2ReferenceYesNo
} from '@/types/api'

export type RoleInBuilding = 'craftsmen' | 'janitors' | 'serviceProvider' | 'delegate' | 'revisor' | 'tenantChange' | 'owner'

export interface HandoverDevice extends Partial<ObjectdeviceDevice> {
  id: string
  internalId?: string | number
  name?: string
  category?: number
  value?: string
  edited?: boolean
  isNewItem?: boolean
  roomId?: string
  referenceId?: string
  ownerId?: string
  order?: number
  room?: string
  installationDate?: TypeDate | null
  previousCondition?: string
  keyData?: DeviceLockingKey
  /** Handover / create-order per-device note text. */
  note?: string
  /** Per-device order description in create-order wizard (step 1 textarea). */
  orderDescription?: string
}

export interface HandoverRoom extends Partial<ObjectroomRoom> {
  id: string
  name: string
  devices: HandoverDevice[]
  referenceId?: string
  order?: number
  edited?: boolean
}

export interface Contact extends Contactcontactv2Company, V2Person {
  canBeSetInactive?: boolean
  owner?: {
    id: number
    name?: string
  }
  allowedAccountIds?: number[]
  isCompany?: boolean
  isOnlyInterestedParty?: boolean
  isEmployee?: boolean
  isTenant?: boolean
  isOwner?: boolean
  hasEmail?: boolean
  roleInBuilding?: RoleInBuilding
}

/**
 * Contact search hit carrying the `objectID` alias. The search read model returns `id`,
 * but call sites migrated off Algolia still key on `objectID`, so the API layer adds it.
 */
export type ContactSearchHit = SearchContactHit & { objectID: string }

export type ContactSearchResponse = Omit<SearchListContactsResponse, 'hits'> & {
  hits: ContactSearchHit[]
}

/**
 * Caller-facing shape of a ListContacts request. Every filter is optional because the
 * endpoint treats an unset field as "no filter", while the generated request type marks
 * all fields required.
 */
export type ContactSearchRequest = Partial<Omit<SearchListContactsRequest, 'accountId'>> & {
  accountId?: string
}

export type ContactSearchFilters = {
  active?: boolean
  contactPersonsHasEmail?: boolean
  hasEmail?: boolean
  isCompany?: boolean
  isEmployee?: boolean
  isOnlyInterestedParty?: boolean
  isOwner?: boolean
  isTenant?: boolean
  industries?: IndustryIndustry[]
  ownerIds?: string[]
  ownerNames?: string[]
  preferredCommunicationChannels?: V2PreferredCommunicationChannel[]
  tags?: string[]
  types?: Contactcontactv2Type[]
  sortEmail?: SortSortOrder
  sortName?: SortSortOrder
  sortNumericId?: SortSortOrder
  sortOwnerName?: SortSortOrder
}

export type ApplicationFormCompany = Omit<Requestapplicationv2Company, 'contactPerson'> & {
  contactPerson: V2CompanyContactPerson
}

export type ApplicationFormIndividual = Requestapplicationv2Individual

/** Individual row in application form Vuex state (API shape + internal UI id). */
export type ApplicationFormStoreIndividual = ApplicationFormIndividual & { internalId: string }

export type ApplicationFormEmployment = Requestapplicationv2Employment

export type ApplicationFormTenancy = Applicationv2Tenancy
export type ApplicationFormTenancyItem = ApplicationFormCompany | ApplicationFormIndividual

export interface ObjectWithCategory {
  category: string
}

export interface TypeDateLike {
  year: number
  month: number
  day: number
}

export interface ApplicationFormCompanyContactPersonDateState {
  dob: string | null
}

export interface ApplicationFormIndividualBasicCityOption {
  city: string
  state: string
}

export interface ApplicationFormIndividualBasicDateState {
  dob: string | null
  residenceStatusValidUntil: string | null
  desiredStartOfContract: string | null
}

export interface ApplicationFormIndividualBasicDataState {
  isCitiesSuggested: boolean
  citiesValue: ApplicationFormIndividualBasicCityOption[]
  dates: ApplicationFormIndividualBasicDateState
  sameAddressAsMainTenant: boolean
  citySelect: ApplicationFormIndividualBasicCityOption | null
}

export interface ApplicationFormTenancyDataState {
  dates: {
    rentedSince: string | null
  }
}

export type CalendarContact = V2Person | Contactcontactv2Company

export type SelectOption = {
    id?: string
    objectID?: string
    name?: string
    firstName?: string
    lastName?: string
    email?: string
    status?: string
    profile?: {
        firstName?: string
        lastName?: string
    }
    contactPersons?: Array<{
        id?: string
        email?: string
        active?: boolean
        firstName?: string
        lastName?: string
    }> | null
}

export type SelectedAttendee = {
    id?: string | null
    objectID?: string
    name?: string
    firstName?: string
    lastName?: string
    email: string
    status?: string
    statusEdit?: boolean
    contactPersons?: SelectOption['contactPersons']
    selectedContactPerson?: string | null
}

export type EventObjectReference = {
    id?: string
    objectID?: string
    buildingId?: string
    name?: string
}

export type UploadedMedia = {
    id: string
    uploadedAt: string
    url?: string
    type?: string | number
    thumbnails?: Array<{ type?: string | number, url?: string }>
}

/** Subset of account payload used by application form legal notice (display name). */
export interface ApplicationFormLegalNoticeAccount {
  company?: { name: string }
  individual?: { firstName: string; lastName: string }
}

/** Document/media row shown in application form upload lists (API or upload response). */
export interface ApplicationFormDocumentMediaRow {
  id: string
  url?: string
  filename?: string
}

export interface ApplicationFormObjectMarketingAvailability {
  type: string
  availableFrom?: number | string
}

/** Marketing object payload for ApplicationFormObjectView (subset of getMarketing response). */
export interface ApplicationFormMarketingObjectViewModel {
  title: string
  address: Record<string, unknown>
  coldRent: number
  additional: number
  heating: number
  rooms: number
  floor: number | string
  publicId: string
  availability: ApplicationFormObjectMarketingAvailability | null
  category: string
  effectiveArea: number
  livingArea: number
  marketingAccountId: string
  images: { mediaId: string; order: number }[]
}

/** Company block on marketing account payloads (ObjectView, ApplicationForm footer, etc.). */
export interface ApplicationFormMarketingCompanySummary {
  name: string
  phoneNumber?: string
  email?: string
}

export interface ApplicationFormMarketingAccountDisplay {
  logoUrl: string
  company: ApplicationFormMarketingCompanySummary
}

/** Marketing account shown next to preferred deposit copy (company or individual name). */
export interface ApplicationFormPreferredDepositMarketingAccount {
  company?: ApplicationFormMarketingCompanySummary
  individual?: { name?: string; firstName?: string; lastName?: string }
}

/** Object deposit block for ApplicationFormPreferredDeposit (subset of marketing object). */
export interface ApplicationFormPreferredDepositObject {
  deposit: {
    amount: number
    cashPaymentAccepted: boolean
    insuranceAccepted: boolean
    insuranceNames: string[]
    bankDepotAccepted: boolean
    bankDepotNames: (string | { iid: string })[]
    bankDepotIds: string[]
    cooperativeShareAccepted: boolean
    bankGuaranteeAccepted: boolean
    bankGuaranteeNames: (string | { iid: string })[]
    bankGuaranteeIds: string[]
    depositNeeded?: boolean
    collectiveInsuranceAccepted?: boolean
  }
}

/** Marketing object loaded for ApplicationForm root (deposit + listing fields). */
export interface ApplicationFormRootMarketingObject extends ApplicationFormPreferredDepositObject {
  marketingAccountId: string
  marketingLanguage?: string
  marketingAssignee?: { phone?: string; email?: string }
  acceptsCreditTrust?: boolean
  category: string
}

export interface ApplicationFormObjectDocumentsContext extends ObjectWithCategory {
  acceptsCreditTrust?: boolean
}

export interface ApplicationFormObjectParkingSlot {
  parkingType: string
  costs: number
}

export interface ApplicationFormObjectMiscContext extends ApplicationFormObjectDocumentsContext {
  parking?: ApplicationFormObjectParkingSlot[]
}

export interface ApplicationFormCompanyBasicDataState {
  isCitiesSuggested: boolean
  citiesValue: ApplicationFormIndividualBasicCityOption[]
  dates: {
    foundedDate: string | null
    desiredStartOfContract: string | null
  }
  citySelect: ApplicationFormIndividualBasicCityOption | null
}

export type GenericOption = {
  id: string
  value?: string
  label?: string
  name?: string
  title?: string
  type?: string
  ownerId?: string
  buildingId?: string
  buildingIds?: string[]
  objectId?: string
  objectIds?: string[]
  referenceId?: string
  objectID?: string
  number?: string
  numericId?: string
  bankName?: string
  mortgageDesignation?: string
  tenancy?: {
    tenant?: {
      contactIds: string[]
    }
  }
  tenants?: Array<{ id: string; name?: string }>
  condominiumOwners?: Array<{ id: string; name?: string }>
  contactPersons?: Contact[]
  company?: {
    name?: string
  }
  individual?: {
    firstName?: string
    lastName?: string
  }
  profile?: {
    firstName?: string
    lastName?: string
  }
}

export type ReferencePostalAddress = {
  street?: string
  streetNumber?: string
  zip?: string
  city?: string
}

export type TicketRelatedReferenceRow = {
  id: string
  name?: string
  address?: ReferencePostalAddress
  characteristics?: { floor?: unknown }
  roomId?: string
}

export type TicketCommentListItem = {
  id: string
} & Record<string, unknown>

export type TicketLinkedMailRow = {
  id: string
  timestamp?: number
  subject?: string
} & Record<string, unknown>

export type TicketAttachmentMediaRow = {
  id?: string
  size?: number
  url?: string
  filename?: string
  mimeType?: string
  media?: StoreMedia
  renderId?: string
  loading?: boolean
}

export type TicketCorrespondenceExtended = TicketCorrespondence & {
  phoneNumbers?: V2PhoneNumber[]
}

export type TicketUiState = Partial<TicketTicket> & {
  key?: string
  id?: string
  references: TicketTicketReference[]
  costs: TicketticketCosts
  listAttachment?: TicketAttachmentMediaRow[]
  correspondences?: TicketCorrespondenceExtended[]
}

export type EmployeeOption = {
  label: string
  value: V2User
  company: string
  active?: boolean
  accountId?: string
  profile?: V2User['profile']
}

export type EmployeeFilterOption = {
  label: string
  value: string
  active?: boolean
  company?: string
  profile?: {
    firstName?: string
    lastName?: string
  }
  accountId?: string
  blocked?: boolean
}

export type EmployeeLabelOption = {
  option?: {
    label?: string
    company?: string
  }
  label?: string
  company?: string
}

export type TicketTemplateOption = Templatev2Template | TicketChecklistTemplate

export type TicketTaskView = TicketChecklistItem & {
  assignee?: EmployeeOption | null
  status: TicketChecklistItem['status'] | ''
}

type ReferenceNamed = {
  id: string
  name?: string
  objectID?: string
  objectId?: string
  condominiumOwnership?: unknown
}

type ReferenceObject = ReferenceNamed & {
  buildingId?: string
  number?: string
  numericId?: string
}

type ReferenceBuilding = ReferenceNamed & {
  objectIds?: string[]
  address?: ReferencePostalAddress
}

type ReferenceProperty = ReferenceNamed & {
  buildingIds?: string[]
}

type ReferenceDevice = ReferenceNamed & {
  objectID?: string
  referenceId: string
  type?: string
  uninstallation?: TypeDate | string | null
  roomId?: string
}

type ReferenceTenancy = ReferenceNamed & {
  objectID?: string
  tenants?: Array<{ id: string; name?: string }>
  tenant?: { contactIds: string[] }
}

type ReferenceCondominium = ReferenceNamed & {
  objectID?: string
  condominiumOwners?: Array<{ id: string; name?: string }>
}

type ReferenceRoom = ReferenceNamed & {
  objectId?: string
}

type ReferenceMortgage = ReferenceNamed & {
  bankName?: string
  mortgageDesignation?: string
}

type ReferenceInsurance = ReferenceNamed & {
  type?: string
}

export type TicketReferenceView = TicketTicketReference & {
  property?: ReferenceProperty
  building?: ReferenceBuilding
  object?: ReferenceObject
  device?: ReferenceDevice
  tenancy?: ReferenceTenancy
  condominium?: ReferenceCondominium
  contact?: ReferenceNamed | Contact
  request?: ReferenceNamed
  room?: ReferenceRoom
  mortgage?: ReferenceMortgage
  insurance?: ReferenceInsurance
  relatedReference?: TicketRelatedReferenceRow[]
  listContact?: Contact[]
  contactPersons?: Contact[]
  condominiumOwnership?: unknown
}

export type TicketLinkView = TicketTicketLink

export type TicketMediaView = StoreMedia

export type TicketEmployee = V2User

export type TicketOwnerAccount = V2Account

export type TicketMailEntry = {
  id: string
  mailType?: string
  referenceId?: string[]
  timestamp?: number
  deliverData?: {
    status?: string
    errorMessage?: string
  }
  engagementData?: {
    status?: string
  }
}

export type TicketConnectionsState = {
  properties: GenericOption[]
  buildings: GenericOption[]
  objects: GenericOption[]
  condominiums: GenericOption[]
  tenancies: GenericOption[]
  devices: GenericOption[]
  contacts: GenericOption[]
  requests: GenericOption[]
  rooms: GenericOption[]
  mortgages: GenericOption[]
  insurances: GenericOption[]
}

export type FileTransmission = {
  id?: string
  createdAt?: number
  processed?: boolean
  editorUrl?: string
  status?: string
  fileName?: string
}

export type KeyValueOption = {
  key: string
  value: string
}

export type AutosuggestSelection<T> = {
  item: T
}

export type TicketConnectionContactHolder = {
  contactIds?: string[]
}

export type TicketReferenceLike = {
  id?: string
  objectID?: string
  referenceId?: string
  name?: string
  owner?: TicketConnectionContactHolder
  tenant?: TicketConnectionContactHolder
  tenants?: TicketConnectionContactHolder[]
  obj?: { id?: string; category?: string; objectCategory?: string }
  object?: { category?: string; objectCategory?: string }
  objectId?: string
  category?: string
  objectCategory?: string
  requesters?: Array<{ contactId?: string }>
  [key: string]: unknown
}

export type TicketConnectionQuery = Record<string, string>

export const REFERENCE_CHECK_QUERY_MODES = {
  NONE: 'NONE',
  AFFORDABILITY: 'AFFORDABILITY',
  COMPLETE_DOSSIER_AND_AFFORDABILITY: 'COMPLETE_DOSSIER_AND_AFFORDABILITY'
} as const

export type ReferenceCheckQueryMode = typeof REFERENCE_CHECK_QUERY_MODES[keyof typeof REFERENCE_CHECK_QUERY_MODES]

export interface ReferenceCheckSettings {
  queryMode: ReferenceCheckQueryMode
  senderMailboxId: string | null
  requireDebtCollectionExtract: boolean
  requireIdDocument: boolean
  maxAffordabilityPercent: number
  tolerancePercent: number
  employerEmailTemplate: string
  landlordEmailTemplate: string
}

export type DigitalReferenceType = 'employer' | 'landlord'

export type DigitalReferenceLogoFields = {
  logoUrl?: string
  accountLogoUrl?: string
  accountId?: string
  marketingAccountId?: string
  ownerId?: string
  objectId?: string
}

export type DigitalReferenceFormResponse = Omit<V2GetReferenceFormResponse, 'administration'> & DigitalReferenceLogoFields & {
  administration?: (V2ReferenceFormAdministration & DigitalReferenceLogoFields) | null
}

export type DigitalReferenceChoiceValue = V2ReferenceConfirmation | V2ReferenceYesNo

export interface DigitalReferenceChoice {
  value: DigitalReferenceChoiceValue
  label: string
}

export interface DigitalReferenceQuestion {
  key: string
  label: string
  applicantValue: string
  value: DigitalReferenceChoiceValue
  choices: DigitalReferenceChoice[]
}
